import { TABLET_API_ORIGIN } from '@/global/env';
import {
  CancelableRequestOptions,
  RestClientInterface,
} from '@/lib/restClient';

import {
  MosPosDiff,
  MosPosFailureCode,
  MosPosImportResult,
  MosPosMenuListData,
  MosPosMenuMasterError,
  MosPosValidateData,
  MosPosValidateResponse,
} from './types';

/** multipart のパート名。バックエンドと一致させてください。 */
const MASTER_PART = 'master_file';
const RELATION_PART = 'relation_file';

type Envelope<T> = {
  ok: boolean;
  data?: T;
  error?: { code?: string; message?: string };
};

type MosPosDiffWire = Partial<MosPosDiff> & {
  New?: unknown;
  Update?: unknown;
  Unchanged?: unknown;
};

const numberOrZero = (value: unknown): number => {
  const result = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(result) ? result : 0;
};

/** Go の json tag 未設定レスポンス（New/Update/Unchanged）にも対応します。 */
const normalizeValidateData = (
  data: MosPosValidateData
): MosPosValidateData => {
  if (!data.diff || typeof data.diff !== 'object') return data;

  const diff = data.diff as MosPosDiffWire;
  return {
    ...data,
    diff: {
      new: numberOrZero(diff.new ?? diff.New),
      update: numberOrZero(diff.update ?? diff.Update),
      unchanged: numberOrZero(diff.unchanged ?? diff.Unchanged),
    },
  };
};

/**
 * リクエストごとに新しい FormData を作ります。使い回すと2回目以降のリクエストに
 * 前回のパートが残ってしまうためです。
 */
const buildImportFormData = (
  masterFile: File,
  relationFile: File
): FormData => {
  const form = new FormData();
  form.append(MASTER_PART, masterFile);
  form.append(RELATION_PART, relationFile);
  return form;
};

/** HTTP status と body の error.code から失敗の種類を決めます。 */
const toFailureCode = (
  status: number,
  body: Envelope<unknown> | undefined
): MosPosFailureCode => {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  const code = body?.error?.code;
  return code ? (code as MosPosFailureCode) : 'UNKNOWN';
};

export class MosPosMenuMasterService {
  constructor(private readonly restClient: RestClientInterface) {}

  public async fetchList(): Promise<MosPosMenuListData> {
    const res = await this.restClient.get<Envelope<MosPosMenuListData>>(
      `${TABLET_API_ORIGIN}/dashboard-api/pos-menu-masters` as const
    );
    if (!res.data) {
      throw new MosPosMenuMasterError('UNKNOWN', 200);
    }
    return res.data;
  }

  /**
   * 2ファイルを検証します。business validation の Error は例外ではなく
   * ok:false の戻り値として返します。preview を出せないだけで、リクエスト自体は
   * 正しく処理されているためです。
   */
  public async validateImport(
    masterFile: File,
    relationFile: File,
    options?: CancelableRequestOptions
  ): Promise<MosPosValidateResponse> {
    const { status, data } = await this.restClient.postFormData<
      Envelope<MosPosValidateData>
    >(
      `${TABLET_API_ORIGIN}/dashboard-api/pos-menu-import/validate` as const,
      buildImportFormData(masterFile, relationFile),
      options
    );

    if (status !== 200) {
      throw new MosPosMenuMasterError(
        toFailureCode(status, data),
        status,
        data?.data
      );
    }
    return {
      ok: Boolean(data?.ok),
      data: normalizeValidateData(data?.data as MosPosValidateData),
    };
  }

  /** 同じ2ファイルを再送信します。バックエンドが検証をやり直します。 */
  public async runImport(
    masterFile: File,
    relationFile: File
  ): Promise<MosPosImportResult> {
    const { status, data } = await this.restClient.postFormData<
      Envelope<{ result: MosPosImportResult } & MosPosValidateData>
    >(
      `${TABLET_API_ORIGIN}/dashboard-api/pos-menu-import/import` as const,
      buildImportFormData(masterFile, relationFile)
    );

    if (status !== 200) {
      throw new MosPosMenuMasterError(
        toFailureCode(status, data),
        status,
        data?.data as MosPosValidateData | undefined
      );
    }
    const result = data?.data?.result;
    if (!result) {
      throw new MosPosMenuMasterError('UNKNOWN', status);
    }
    return result;
  }
}
